import MapDisplay from "components/MapDisplay";
import MapLabel from "components/MapLabel";
import moment from "moment";
import React, { useMemo } from "react";
import { useIntl } from "react-intl";
import { connect } from "react-redux";
import CommonUtils from "services/common/utils";
import MapUtils from "services/map/utils";
import ParkkiTheme from "themes/ParkkiTheme";
import DisplayTypes from "types/DisplayTypes";

type Props = {
  loading?: boolean;
  currentFloor: number;
  options: {
    items: {
      display_id: string;
      reqFilters: { from: number; to: number };
    }[];
  };
  filters: {
    from: number;
    to: number;
  };
  map: {
    initialized: boolean;
    displays: DisplayTypes[];
    selectedLayers: { id: string }[];
  };
  isDisplaySelected?: Function;
  isLabelDisplayed?: Function;
  hasLabelBackground?: Function;
  onSelectLayers: Function;
  getLabelIcon?: Function;
  getLabelLength?: Function;
  getLabel?: Function;
  getLabelColor: Function;
  getBackground?: Function;
  getScrolling: Function;
  getAlternating?: Function;
  getMaxDigits: Function;
  mapConfig: { labelsDisplayed?: boolean };
  disabled?: boolean;
};
const Y_OFFSET_WITH_TITLE = 62;
const Y_OFFSET_WITHOUT_TITLE = 46;
function DisplaysLayers({
  currentFloor,
  filters,
  options,
  map,
  loading,
  isDisplaySelected,
  isLabelDisplayed,
  hasLabelBackground,
  getLabelIcon,
  getLabel,
  getLabelLength,
  getLabelColor,
  mapConfig,
  getScrolling,
  getAlternating,
  getMaxDigits,
  onSelectLayers,
  disabled = false,
}: Props): JSX.Element[] {
  const intl = useIntl();
  const selectedLayers = useMemo(() => {
    return map?.selectedLayers || [];
  }, [map?.selectedLayers]);

  const formattedDisplays: DisplayTypes[][] = useMemo(() => {
    const sortedDisplays = map?.displays
      ?.filter((display) => display?.meta_data?.display !== false)
      ?.filter((display) =>
        currentFloor || currentFloor === 0
          ? display.floor_level === currentFloor
          : display
      )
      ?.reduce((previous: any, currentDisplay) => {
        // @ts-ignore
        const currentKey = `${currentDisplay?.position?.lat}-${currentDisplay?.position?.lng}`;
        const displaysWithSameCoordinates = previous?.[currentKey] || [];
        return {
          ...previous,
          [currentKey]: [...displaysWithSameCoordinates, currentDisplay],
        };
      }, {});
    return Object.values(sortedDisplays || {});
  }, [currentFloor, map?.displays]);

  function handleIsDisplaySelected(display: DisplayTypes): boolean {
    if (isDisplaySelected) {
      return isDisplaySelected(display);
    }
    return !!selectedLayers.find((itm) => display?.id === itm?.id);
  }

  function handleIsLabelDisplayed(display: DisplayTypes) {
    if (isLabelDisplayed) {
      return (
        mapConfig?.labelsDisplayed &&
        isLabelDisplayed({
          ...display,
          selected: handleIsDisplaySelected(display),
        })
      );
    }
    return mapConfig?.labelsDisplayed;
  }

  function handleGetMaxDigits(display: DisplayTypes) {
    if (getMaxDigits) {
      return getMaxDigits(display);
    }
    return display?.meta_data?.max_digits;
  }

  function handleGetScrolling(display: DisplayTypes) {
    if (getScrolling) {
      return getScrolling(display);
    }
    return display?.meta_data?.scrolling;
  }

  function handleGetAlternating(display: DisplayTypes) {
    if (getAlternating) {
      return getAlternating(display);
    }
    return null;
  }

  function handleHasLabelBackground(display: DisplayTypes) {
    if (hasLabelBackground) {
      return (
        mapConfig?.labelsDisplayed &&
        hasLabelBackground({
          ...display,
          selected: handleIsDisplaySelected(display),
        })
      );
    }
    return true;
  }

  function handleDisplayClick(display: DisplayTypes) {
    const newSelectedLayer = {
      ...display,
      type: "display",
    };

    const alreadySelected = !!selectedLayers.find(
      (itm) => display?.id === itm?.id
    );

    const newSelectedLayers = alreadySelected ? [] : [newSelectedLayer];
    const isSelection = newSelectedLayers?.length > selectedLayers?.length;

    return onSelectLayers(newSelectedLayers, newSelectedLayer, isSelection);
  }

  const getColorFromDuration = (status: string, duration: number) => {
    if (status === "STAND_BY") {
      return ParkkiTheme.colors.darkGrey;
    }
    // 10 minutes
    if (duration < 600) {
      return ParkkiTheme.colors.green;
    } else if (duration >= 600 && duration < 1800) {
      // 10 minutes >= DURATION < 30 minutes
      return ParkkiTheme.colors.orange;
    }
    // >= 30 minutes
    return ParkkiTheme.colors.red;
  };

  if (!map?.initialized || loading) {
    return [];
  }

  // @ts-ignore
  return formattedDisplays.map((displays: Array) => {
    let currentYOffset = 0;
    const currentDisplays = displays
      ?.filter((item: DisplayTypes) => !item?.meta_data?.is_pictogram)
      // @ts-ignore
      ?.sort((a, b) => {
        const currentIndex =
          a.meta_data?.index >= 0 ? a.meta_data?.index : a.index;
        const otherIndex =
          b.meta_data?.index >= 0 ? b.meta_data?.index : b.index;
        if (currentIndex > otherIndex) {
          return -1;
        }
        if (currentIndex < otherIndex) {
          return 1;
        }
        return 0;
      });
    return currentDisplays?.map((display: DisplayTypes, index: number) => {
      const label = getLabel ? getLabel(display) : null;
      const selected = handleIsDisplaySelected(display);
      const formattedOptions = options?.items?.find((item) => {
        return item.display_id === display.id;
      });
      const previousHasTitle = !!currentDisplays?.[index - 1]?.meta_data?.label;
      const title = display?.meta_data?.label;
      if (index > 0) {
        currentYOffset += previousHasTitle
          ? Y_OFFSET_WITH_TITLE
          : Y_OFFSET_WITHOUT_TITLE;
      }
      const duration = moment().diff(
        moment(
          display?.last_received_value_date ?? display?.last_sent_value_date
        ),
        "seconds"
      );
      const message =
        display?.status === "STAND_BY"
          ? `${intl.formatMessage({
              id: "displaysScene.standbyMode",
            })} ${CommonUtils.formatDuration(duration)}`
          : `${intl.formatMessage({
              id: "displaysScene.runningMode",
            })} ${CommonUtils.formatDuration(duration)}`;
      const alternatingMessages = handleGetAlternating(display);
      return (
        <MapDisplay
          title={title}
          offset={[0, currentYOffset]}
          key={display.id}
          position={MapUtils.getNormalizedCoordinates(display?.position || [])}
          onClick={() => handleDisplayClick(display)}
          selected={selected}
          options={{
            ...(display?.options || {}),
            ...formattedOptions,
            tooltipCoordinates: display?.display?.location,
            labelColor: getLabelColor(display),
            labelLength: getLabelLength ? getLabelLength(display) : undefined,
            withLabel: handleIsLabelDisplayed(display),
            maxDigits: handleGetMaxDigits(display),
            scrolling: handleGetScrolling(display),
            alternatingMessages: alternatingMessages,
            withLabelBackground: handleHasLabelBackground({
              ...display,
              options: { ...display?.options, ...formattedOptions },
            }),
          }}
          label={
            label ? (
              <MapLabel layer={display} getIcon={getLabelIcon} label={label} />
            ) : null
          }
          statusColor={getColorFromDuration(display?.status, duration)}
          statusHelper={message}
          disabled={disabled}
        />
      );
    });
  });
}
const mapStateToProps = (state: any) => ({
  zoom: MapUtils.getZoom(state.Map),
  currentFloor: MapUtils.getFloor(state.Map),
  mapConfig: state.Map.config,
});
// @ts-ignore
export default connect(mapStateToProps)(DisplaysLayers);
